import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Player, type PlayerRef } from '@remotion/player';
import {
  VideoStudioCompositie,
  type CompositieProps,
} from '@video-studio/renderer';
import {
  berekenDuurInFrames,
  frameNaarSeconde,
  secondeNaarFrame,
  snapDoelen,
  snapFrame,
  zoekItem,
  type TimelineItem,
} from '@video-studio/shared';
import { api, ApiFoutException, type ExportItem, type MediaDto, type Taak } from '../api/client';
import { leesGesleeptId, spoorUitSleepTypes, zetMediaOpTijdlijn } from '../state/plaatsMedia';
import { useEditor } from '../state/useEditor';
import { Mediapaneel } from './panelen/Mediapaneel';
import { Eigenschappenpaneel } from './panelen/Eigenschappenpaneel';
import { Tijdlijn } from './Tijdlijn';
import { Taakbalk } from './Taakbalk';
import { Exportdialoog } from './Exportdialoog';
import { TaakMeldingen } from './TaakMeldingen';

export interface EditorProps {
  projectId: string;
  onTerug: () => void;
  onUitloggen?: () => void;
}

/**
 * De editor.
 *
 * De preview gebruikt de Remotion Player met exact dezelfde compositie als de
 * export. Daardoor kan de preview niet afwijken van het eindresultaat.
 *
 * De speler wordt bewust NIET opnieuw getekend bij elke wijziging van de
 * selectie: de Player staat in een eigen component die alleen van het project
 * afhangt. Dat is de aanbevolen werkwijze van Remotion en voorkomt haperend
 * afspelen tijdens het monteren.
 */
export function Editor({ projectId, onTerug, onUitloggen }: EditorProps) {
  const editor = useEditor(projectId);
  const { project, mediaMap, selectie, duurInFrames } = editor;

  const playerRef = useRef<PlayerRef>(null);
  const volgFrame = useRef<number | null>(null);
  const [huidigFrame, setHuidigFrame] = useState(0);
  const [speelt, setSpeelt] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [snapping, setSnapping] = useState(true);
  const [beeldDoel, setBeeldDoel] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportTaken, setExportTaken] = useState<Taak[]>([]);
  const [exports, setExports] = useState<ExportItem[]>([]);
  const [zijpaneel, setZijpaneel] = useState<
    'media' | 'tekst' | 'audio' | 'templates' | 'ai' | 'montage'
  >('media');

  useEffect(() => {
    void editor.laadProject(projectId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Spelergebeurtenissen volgen zodat de tijdlijn en het frameveld kloppen.
  useEffect(() => {
    const speler = playerRef.current;
    if (!speler) return;
    const bijFrame = (e: { detail: { frame: number } }) => setHuidigFrame(e.detail.frame);
    const bijPlay = () => setSpeelt(true);
    const bijPause = () => setSpeelt(false);
    speler.addEventListener('frameupdate', bijFrame);
    speler.addEventListener('play', bijPlay);
    speler.addEventListener('pause', bijPause);
    return () => {
      speler.removeEventListener('frameupdate', bijFrame);
      speler.removeEventListener('play', bijPlay);
      speler.removeEventListener('pause', bijPause);
    };
  }, [project?.id]);

  const duur = Math.max(1, duurInFrames);

  /** Media-URL's voor de compositie. Bij voorkeur de lichte proxy. */
  const assetUrls = useMemo(() => {
    const urls: Record<string, string> = {};
    if (!project) return urls;
    const gebruikte = new Set<string>();
    for (const track of project.tracks) {
      for (const item of track.items) {
        if ('assetId' in item) gebruikte.add(item.assetId);
      }
    }
    for (const id of gebruikte) {
      const media = mediaMap.get(id);
      if (!media) continue;
      urls[id] = media.heeftProxy && media.proxyUrl && media.soort === 'video' ? media.proxyUrl : media.bronUrl;
    }
    return urls;
  }, [project, mediaMap]);

  const assetFps = useMemo(() => {
    const fps: Record<string, number | null> = {};
    for (const [id, media] of mediaMap) {
      fps[id] = media.inspectie?.fps ?? null;
    }
    return fps;
  }, [mediaMap]);

  const compositieProps = useMemo<CompositieProps>(
    () =>
      project
        ? { project, assetUrls, assetFps, duurInFrames: Math.max(1, duurInFrames) }
        : { project: leegProject(), assetUrls: {}, duurInFrames: 30 },
    [project, assetUrls, assetFps, duurInFrames],
  );

  const springNaar = useCallback(
    (frame: number) => {
      const doel = Math.max(0, Math.round(frame));
      // Een net toegevoegde clip kan voorbij de huidige duur vallen. De speler
      // krijgt die duur pas na de volgende render, dus dan zoeken we alsnog.
      if (doel > duur - 1) {
        volgFrame.current = doel;
        setHuidigFrame(doel);
        return;
      }
      volgFrame.current = null;
      playerRef.current?.seekTo(doel);
      setHuidigFrame(doel);
    },
    [duur],
  );

  useEffect(() => {
    const doel = volgFrame.current;
    if (doel === null || doel > duur - 1) return;
    volgFrame.current = null;
    const id = window.requestAnimationFrame(() => {
      playerRef.current?.seekTo(doel);
      setHuidigFrame(doel);
    });
    return () => window.cancelAnimationFrame(id);
  }, [duur]);

  const wisselAfspelen = useCallback(
    (e?: React.MouseEvent | React.KeyboardEvent) => {
      const speler = playerRef.current;
      if (!speler) return;
      if (speelt) {
        speler.pause();
      } else {
        // Het gebaar meegeven voorkomt dat de browser het afspelen blokkeert.
        speler.play(e);
      }
    },
    [speelt],
  );

  // Sneltoetsen. Alleen actief als er niet in een invoerveld wordt getypt.
  useEffect(() => {
    const bijToets = (e: KeyboardEvent) => {
      const doel = e.target as HTMLElement | null;
      const inInvoer =
        doel &&
        (doel.tagName === 'INPUT' || doel.tagName === 'TEXTAREA' || doel.isContentEditable);
      if (e.key === ' ' && !inInvoer) {
        e.preventDefault();
        wisselAfspelen();
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && !e.shiftKey) {
        e.preventDefault();
        editor.undo();
      }
      if ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === 'y' || (e.shiftKey && e.key.toLowerCase() === 'z'))) {
        e.preventDefault();
        editor.redo();
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        void editor.bewaarNu();
      }
      if (e.key === 'ArrowLeft' && !inInvoer) {
        e.preventDefault();
        springNaar(huidigFrame - (e.shiftKey ? 10 : 1));
      }
      if (e.key === 'ArrowRight' && !inInvoer) {
        e.preventDefault();
        springNaar(huidigFrame + (e.shiftKey ? 10 : 1));
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectie && !inInvoer) {
        e.preventDefault();
        editor.bewerk((p) => ({
          ...p,
          tracks: p.tracks.map((t) => ({ ...t, items: t.items.filter((i) => i.id !== selectie) })),
        }), 'Laag verwijderen');
        editor.selecteer(null);
      }
    };
    window.addEventListener('keydown', bijToets);
    return () => window.removeEventListener('keydown', bijToets);
  }, [editor, huidigFrame, selectie, springNaar, wisselAfspelen]);

  const verversExports = useCallback(async () => {
    try {
      const resultaat = await api.exports(projectId);
      setExports(resultaat.items);
    } catch {
      // Niet kritiek.
    }
  }, [projectId]);

  useEffect(() => {
    void verversExports();
  }, [verversExports]);

  const geselecteerdItem: TimelineItem | null = useMemo(() => {
    if (!project || !selectie) return null;
    return zoekItem(project, selectie)?.item ?? null;
  }, [project, selectie]);

  if (editor.fout) {
    return (
      <div className="startscherm">
        <div className="melding melding--fout">{editor.fout}</div>
        <button type="button" className="knop" onClick={onTerug}>
          Terug naar projecten
        </button>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="laadscherm">
        <span>Project laden…</span>
      </div>
    );
  }

  return (
    <div className="editor">
      <Taakbalk
        editor={editor}
        project={project}
        duurInFrames={duurInFrames}
        onTerug={onTerug}
        onExport={() => setExportOpen(true)}
        onUitloggen={onUitloggen}
        onBeeldformaat={(breedte, hoogte, formaat) =>
          editor.bewerk(
            (p) => ({ ...p, breedte, hoogte, beeldformaat: formaat }),
            'Beeldformaat wijzigen',
          )
        }
      />

      <div className="editor__midden">
        <Mediapaneel
          editor={editor}
          actief={zijpaneel}
          onWissel={setZijpaneel}
          opFrame={huidigFrame}
          naarFrame={springNaar}
          duurInFrames={duur}
        />

        <main
          className={`voorvertoning ${beeldDoel ? 'voorvertoning--doel' : ''}`}
          onDragOver={(e) => {
            if (!spoorUitSleepTypes(e.dataTransfer.types)) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = 'copy';
            if (!beeldDoel) setBeeldDoel(true);
          }}
          onDragLeave={(e) => {
            if (e.currentTarget.contains(e.relatedTarget as Node)) return;
            setBeeldDoel(false);
          }}
          onDrop={(e) => {
            if (!spoorUitSleepTypes(e.dataTransfer.types)) return;
            e.preventDefault();
            setBeeldDoel(false);
            const item = mediaMap.get(leesGesleeptId(e.dataTransfer));
            if (!item) return;
            zetMediaOpTijdlijn(editor, item, huidigFrame, springNaar);
          }}
        >
          <div className="voorvertoning__kader" style={{ aspectRatio: `${project.breedte} / ${project.hoogte}` }}>
            <Player
              ref={playerRef}
              component={VideoStudioCompositie}
              inputProps={compositieProps}
              durationInFrames={duur}
              fps={project.fps}
              compositionWidth={project.breedte}
              compositionHeight={project.hoogte}
              style={{ width: '100%', height: '100%' }}
              controls={false}
              acknowledgeRemotionLicense
              clickToPlay={false}
              numberOfSharedAudioTags={0}
            />
            {project.tracks.every((track) => track.items.length === 0) && (
              <p className="voorvertoning__leeg">Sleep een video uit de bibliotheek hierheen</p>
            )}
          </div>

          <div className="transport">
            <button
              type="button"
              className="knop knop--rond"
              title="Naar het begin"
              onClick={() => springNaar(0)}
            >
              ⏮
            </button>
            <button
              type="button"
              className="knop knop--rond"
              title="Vorige frame (pijltje links)"
              onClick={() => springNaar(huidigFrame - 1)}
            >
              ◀
            </button>
            <button
              type="button"
              className="knop knop--primair knop--rond"
              title="Afspelen / pauzeren (spatiebalk)"
              onClick={(e) => wisselAfspelen(e)}
            >
              {speelt ? '❚❚' : '▶'}
            </button>
            <button
              type="button"
              className="knop knop--rond"
              title="Volgende frame (pijltje rechts)"
              onClick={() => springNaar(huidigFrame + 1)}
            >
              ▶
            </button>
            <button
              type="button"
              className="knop knop--rond"
              title="Naar het einde"
              onClick={() => springNaar(duur - 1)}
            >
              ⏭
            </button>

            <label className="transport__tijd">
              <span className="transport__label">Tijd</span>
              <input
                type="number"
                step="0.1"
                min={0}
                max={frameNaarSeconde(duur, project.fps)}
                value={Number(frameNaarSeconde(huidigFrame, project.fps).toFixed(2))}
                onChange={(e) => springNaar(secondeNaarFrame(Number(e.target.value), project.fps))}
              />
              <span className="transport__eenheid">s</span>
            </label>

            <span className="transport__frame">
              frame {huidigFrame} / {duur - 1}
            </span>
          </div>
        </main>

        <Eigenschappenpaneel
          editor={editor}
          project={project}
          item={geselecteerdItem}
          mediaMap={mediaMap}
          opFrame={huidigFrame}
        />
      </div>

      <Tijdlijn
        editor={editor}
        project={project}
        duurInFrames={duur}
        huidigFrame={huidigFrame}
        opFrame={springNaar}
        zoom={zoom}
        opZoom={setZoom}
        snapping={snapping}
        opSnapping={setSnapping}
        mediaMap={mediaMap}
        selectie={selectie}
        opSelectie={editor.selecteer}
      />

      <TaakMeldingen
        taken={exportTaken}
        onOpnieuw={(taak) => setExportTaken((t) => t.filter((x) => x.id !== taak.id))}
      />

      {exportOpen && (
        <Exportdialoog
          project={project}
          duurInFrames={duur}
          onSluiten={() => setExportOpen(false)}
          onGestart={(taak) => setExportTaken((t) => [...t, taak])}
          onKlaar={() => void verversExports()}
          exports={exports}
        />
      )}
    </div>
  );
}

function leegProject() {
  return {
    id: 'leeg',
    naam: 'Leeg',
    versie: 1,
    schemaVersie: 1 as const,
    fps: 30,
    beeldformaat: '16:9' as const,
    breedte: 1920,
    hoogte: 1080,
    achtergrond: { kleur: '#000000' },
    tracks: [],
    duurOverrideInFrames: null,
    gemaaktOp: new Date(0).toISOString(),
    gewijzigdOp: new Date(0).toISOString(),
  };
}

export { berekenDuurInFrames, snapDoelen, snapFrame };
export type { MediaDto, ApiFoutException };
